/**
 * Greeting: saludo + título + meta de último acceso.
 *
 * Sprint 11 v2 S11-V2-05 amplía la versión base de S11-V2-03 con prop
 * `alerta` opcional para el Greeting split del Dashboard del vecino.
 *
 * Modo:
 * - **Sin `alerta`**: render single-column como mock del handoff base.
 * - **Con `alerta`**: layout 2 columnas (izquierda eyebrow/title/meta;
 *   derecha tarjeta con fondo según severidad). En mobile (<720px)
 *   las 2 columnas se apilan: izquierda arriba, alerta abajo.
 *
 * Severidades del lado derecho (decisión de S11-V2-05 + S10-13 doctrina
 * de voz pasiva impersonal):
 * - `warning`: deudor o moroso. Fondo crema + texto on-warning-soft.
 * - `danger`: suspendible o suspendida. Fondo rojizo soft + texto
 *   on-danger.
 */
import type { ReactNode } from 'react';

export type AlertaSeveridad = 'warning' | 'danger';

interface AlertaProps {
  /** Título corto, ej: "Cuota semestral impaga". */
  titulo: ReactNode;
  /** Mensaje explicativo. Voz pasiva impersonal: "Se podrían suspender..." */
  mensaje: ReactNode;
  severidad: AlertaSeveridad;
}

interface GreetingProps {
  eyebrow: ReactNode;
  title: ReactNode;
  meta?: ReactNode;
  alerta?: AlertaProps;
}

const ALERTA_CLASSES: Record<AlertaSeveridad, { bg: string; text: string; subText: string }> = {
  warning: {
    bg: 'bg-soft-warning',
    text: 'text-text-on-warning',
    subText: 'text-text-on-warning-soft',
  },
  danger: {
    bg: 'bg-soft-danger',
    text: 'text-text-on-danger',
    subText: 'text-text-on-danger',
  },
};

export function Greeting({ eyebrow, title, meta, alerta }: GreetingProps) {
  const izquierda = (
    <div className="flex-1 min-w-0">
      <div className="text-sm text-text-muted uppercase tracking-wide mb-1">
        {eyebrow}
      </div>
      <h1 className="text-2xl font-medium text-text-primary leading-tight">{title}</h1>
      {meta && <div className="text-base text-text-secondary mt-1">{meta}</div>}
    </div>
  );

  if (!alerta) {
    return (
      <div className="bg-bg-sunken border-b-[0.5px] border-white/[0.08] px-4 md:px-8 pt-6 pb-5">
        {izquierda}
      </div>
    );
  }

  const { bg, text, subText } = ALERTA_CLASSES[alerta.severidad];

  return (
    <div className="bg-bg-sunken border-b-[0.5px] border-white/[0.08] px-4 md:px-8 pt-6 pb-5">
      <div className="flex flex-col md:flex-row gap-4 md:gap-6 md:items-start">
        {izquierda}
        <div
          className={`md:max-w-sm rounded-md p-4 ${bg}`}
          role="alert"
          aria-live="polite"
        >
          <div className={`text-base font-medium mb-1 ${text}`}>
            <span aria-hidden="true">⚠ </span>
            {alerta.titulo}
          </div>
          <div className={`text-sm ${subText}`}>{alerta.mensaje}</div>
        </div>
      </div>
    </div>
  );
}
